import { useEffect, useState, useRef } from 'react';
import type { Meta, StoryFn } from '@storybook/react';
import { action } from '@storybook/addon-actions';

import { CaseHierarchy, type CaseHierarchyProps } from '@pega/cosmos-react-work';
import { createUID, treeHelpers } from '@pega/cosmos-react-core';

export default {
  title: 'Work/CaseHierarchy',
  component: CaseHierarchy
} as Meta;

interface CaseHierarchyStoryArgs
  extends Pick<CaseHierarchyProps, 'lined' | 'icon' | 'heading' | 'error'> {
  numCases: number;
  numChildCases: number;
  currentCaseId?: string;
  loading?: boolean;
  loadMore?: boolean;
  nodes?: CaseHierarchyProps['nodes'];
}

export const CaseHierarchyDemo: StoryFn<CaseHierarchyStoryArgs> = (
  args: CaseHierarchyStoryArgs
) => {
  const [currentNodeId, setCurrentNodeId] = useState<string | undefined>(args.currentCaseId);
  const [allNodes, setAllNodes] = useState<CaseHierarchyProps['nodes']>([]);
  const allNodesRef = useRef<CaseHierarchyProps['nodes']>([]);
  const [loading, setLoading] = useState<boolean>(args.loading ?? true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const getMockNodes = ({
    length = args.numChildCases,
    parent = true
  }: {
    length?: number;
    parent?: boolean;
  } = {}): CaseHierarchyProps['nodes'] => {
    return Array.from({ length }, (_, i) => {
      const isParent = parent && i !== 2;
      const id = createUID();
      const generateRandomCaseId = () =>
        'EPIC-'.concat(String(Math.floor(100000 + Math.random() * 900000)));
      const generateRandomChildCaseId = () =>
        'US-'.concat(String(Math.floor(100000 + Math.random() * 900000)));
      return {
        id,
        label: isParent ? 'Parent Case Type' : 'Child Case Type',
        secondary: isParent ? generateRandomCaseId() : generateRandomChildCaseId(),
        nodes: isParent ? [] : undefined,
        onClick: isParent
          ? undefined
          : nodeId => {
              action(`Action for node: ${nodeId}`)();
            },
        href: id === currentNodeId ? undefined : `#${id}`,
        onPreview: isParent ? () => {} : undefined,
        onLoadMore:
          isParent && args.loadMore
            ? () => {
                const currentNode = treeHelpers.getNode(allNodesRef.current, id);
                if (currentNode && currentNode.nodes) {
                  currentNode.nodes = [
                    ...currentNode.nodes,
                    ...getMockNodes({ length: args.numChildCases, parent: false })
                  ];
                  setAllNodes([...allNodesRef.current]);
                }
              }
            : undefined
      };
    });
  };

  const getNodesCount = (nodes: CaseHierarchyProps['nodes']): number => {
    let nodeCount = 0;
    treeHelpers.forEachNode(nodes, () => {
      nodeCount += 1;
    });
    return nodeCount;
  };

  useEffect(() => {
    allNodesRef.current = allNodes;
  }, [allNodes]);

  useEffect(() => {
    setAllNodes(() => {
      if (args.nodes) return args.nodes;
      return getMockNodes({ length: args.numCases });
    });
  }, []);

  useEffect(() => {
    if (args.nodes) return;
    if (args.numCases > 0) {
      setAllNodes(getMockNodes({ length: args.numCases }));
    }
  }, [args.numCases, args.numChildCases, args.nodes, args.loadMore]);

  const loadChildNodes = (id: string) => {
    setTimeout(() => {
      setAllNodes(tree => {
        return treeHelpers.mapNode(tree, id, node => {
          return {
            ...node,
            loading: false,
            nodes: [...(node.nodes ?? []), ...getMockNodes()]
          };
        });
      });
    }, 1000);
  };

  return (
    <CaseHierarchy
      heading={args.heading ?? 'Case relationships'}
      icon={args.icon ?? 'folder-nested'}
      lined={args.lined}
      error={args.error}
      currentCaseId={args.currentCaseId}
      count={getNodesCount(allNodes)}
      nodes={allNodes}
      loading={args.loading ? args.loading : loading}
      onLoadMore={
        args.loadMore
          ? () =>
              setAllNodes([...allNodesRef.current, ...getMockNodes({ length: args.numChildCases })])
          : undefined
      }
      onNodeClick={(id, e) => {
        e.preventDefault();
        setCurrentNodeId(id);
      }}
      onNodeToggle={id => {
        const clickedNode = treeHelpers.getNode(allNodes, id);
        // If a leaf node, just set to current
        if (!clickedNode?.nodes) return;

        setAllNodes(tree =>
          treeHelpers.mapNode(tree, id, node => {
            return {
              ...node,
              expanded: !node.expanded,
              loading: node.nodes?.length === 0
            };
          })
        );

        // If a parent node with children already loaded, we're done.
        if (clickedNode?.nodes?.length > 0) return;

        loadChildNodes(id);
      }}
      selectableParents
    />
  );
};

CaseHierarchyDemo.args = {
  loadMore: true,
  lined: false,
  numCases: 3,
  numChildCases: 3
};

CaseHierarchyDemo.argTypes = {
  loadMore: { control: { type: 'boolean' } },
  lined: { control: { type: 'boolean' } },
  numCases: { control: { type: 'number' } },
  numChildCases: { control: { type: 'number' } }
};
